import Tour from '@core/components/Tour';
import { useReduxLoading } from '@core/hooks';
import { useApiUtils } from '@core/util/apiUtils';
import { servicosTour } from '@core/util/mockTourUtils';
import FusePageCarded from '@fuse/core/FusePageCarded';
import FuseUtils from '@fuse/utils';
import { cancelarAlert, excluirAlert } from 'app/services/alerts';
import { servicoApi } from 'app/services/api/servicoApi';
import { usuarioApi } from 'app/services/api/usuarioApi';
import * as Actions from 'app/store/actions';
import { GlobalContext } from 'app/store/globalcontext';
import { eventReactGA, initializeReactGA } from 'app/utils/analyticsUtil';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { EditarServicoModal, ServicoSearchHeader, ServicoTable, VisualizarServicoModal } from './components';
import ServicoBuscaAvancadaModal from './components/ServicoBuscaAvancada/ServicoBuscaAvancadaModal';
import { steps } from './components/steps/ServicoSteps';
import { ServicoContext, ServicoProvider } from './ServicoContext';

const SERVICO = 'Serviço';

const Servico = () => {
  const [, setServicoAtual] = useContext(ServicoContext).servicoAtual;
  const [, setSelecao] = useContext(ServicoContext).selecao;
  const [, setServicos] = useContext(ServicoContext).servicos;
  const [, setTotalElements] = useContext(ServicoContext).totalElements;
  const [loading, setLoading] = useContext(ServicoContext).loading;
  const [search] = useContext(ServicoContext).search;
  const [page, setPage] = useContext(ServicoContext).page;
  const [size, setSize] = useContext(ServicoContext).size;
  const [sort] = useContext(ServicoContext).sort;
  const [tipoSelecao, setTipoSelecao] = useContext(ServicoContext).tipoSelecao;
  const [filtroAvancado] = useContext(ServicoContext).filtroAvancado;
  const [funcionalidadesTour, setFuncionalidadesTour] = useContext(GlobalContext).funcionalidadesTour;
  const [idNotificacao, setIdNotificacao] = useState('');
  const [openLoadingModal, setOpenLoadingModal] = useState(false);
  const [progressoLoading, setProgressoLoading] = useState(0);

  const funcaoCloseRef = useRef(null);

  const [run, setRun] = useState(false);
  const [onTour, setTour] = useState(false);

  const history = useHistory();

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  useReduxLoading(loading);

  useEffect(() => {
    initializeReactGA(history?.location?.pathname);
    if (!funcionalidadesTour) {
      setFuncionalidadesTour([]);
    }
    buscarTour();
    // Efeito de montagem: incluir as dependências reexecutaria a carga inicial a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSuccess = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'success' }));
  };

  const resetSelecao = () => {
    setSelecao([]);
    setTipoSelecao('NENHUM');
  };

  const handleSelectionChange = (selection) => {
    setSelecao(selection);
  };

  const initialPage = {
    page,
    q: '',
    size,
    sort: 'descricao,ASC',
  };

  const buscarTour = async () => {
    const funcionalidadeJaExistente = funcionalidadesTour?.some((e) => e === 'SERVICOS');
    if (!funcionalidadeJaExistente) {
      setRun(true);
      setTour(true);
      carregarDadosTour();
    } else {
      fetchServicos();
    }
  };

  const carregarDadosTour = () => {
    setSelecao([]);
    setPage(1);
    setSize(servicosTour.length);
    setTotalElements(3);
    setServicos(servicosTour);
    setLoading(false);
  };

  const fetchServicos = async (search = initialPage) => {
    setLoading(true);
    try {
      if (!search?.filtroAvancado) {
        search.filtroAvancado = filtroAvancado;
      }
      const response = await servicoApi.get(search);
      setSelecao([]);
      setPage(response.data.number);
      setSize(response.data.size);
      setTotalElements(response.data.totalElements);
      setServicos(response.data.content);
    } catch (err) {
      apiUtils.handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchServico = async (servicoId) => {
    setLoading(true);
    try {
      const response = await servicoApi.getOne(servicoId);
      setServicoAtual(response.data);
    } catch (err) {
      apiUtils.handleApiError(err);
    }
    setLoading(false);
  };

  const handleSubmit = async (servico, pageable) => {
    let res;
    let message;

    setLoading(true);

    try {
      if (servico?.id) {
        eventReactGA(SERVICO, 'Atualizar serviço');
        res = await servicoApi.put(servico);
        message = 'Serviço editado com sucesso.';
      } else {
        eventReactGA(SERVICO, 'Cadastrar serviço');
        res = await servicoApi.post(servico);
        message = 'Serviço salvo com sucesso';
      }

      showSuccess(message);

      resetSelecao();
      fetchServicos(pageable);
    } catch (err) {
      if (err?.response?.data?.errorKey === 'servico.possuiAlteracaoFiscal') {
        cancelarAlert({
          opcoes: {
            title:
              'As alíquotas ou as retenções do serviço foram alteradas. Deseja aplicar essas alterações aos contratos vigentes e as vendas?',
            text: '',
          },
          confirmar: async () => {
            setLoading(true);
            try {
              if (servico?.id) {
                eventReactGA(SERVICO, 'Atualizar serviço');
                const chaveNotificacao = FuseUtils.generateGUID();
                setIdNotificacao(chaveNotificacao);
                setProgressoLoading(0);
                setOpenLoadingModal(true);

                res = await servicoApi.put(servico, true, true, chaveNotificacao);
                resetSelecao();
                fetchServicos(pageable);
              }
            } catch (err) {
              apiUtils.handleApiError(err);
            }
          },
          cancelar: async () => {
            setLoading(true);
            try {
              if (servico?.id) {
                eventReactGA(SERVICO, 'Atualizar serviço');
                res = await servicoApi.put(servico, false, true);
                message = 'Serviço editado com sucesso.';
                showSuccess(message);
                resetSelecao();
                fetchServicos(pageable);
                if (funcaoCloseRef.current) {
                  funcaoCloseRef.current();
                }
              }
            } catch (err) {
              apiUtils.handleApiError(err);
            }
          },
        });
      } else {
        apiUtils.handleApiError(err);
      }
    }
    setLoading(false);

    return res;
  };

  const handleDelete = (ids) => {
    excluirAlert({
      confirmar: async () => {
        setLoading(true);
        try {
          eventReactGA(SERVICO, 'Excluir serviço');
          await servicoApi.delete(ids.join(','), tipoSelecao, search);

          showSuccess(ids.length === 1 ? 'Serviço excluído com sucesso' : 'Serviços excluídos com sucesso');
          resetSelecao();
          fetchServicos();
        } catch (err) {
          apiUtils.handleApiError(err);
        }

        setLoading(false);
      },
    });
  };

  const handleInativar = (ids, pageable) => {
    excluirAlert({
      confirmar: async () => {
        try {
          const filtro = {
            ids: ids.map((_servico) => _servico.id),
            search,
            tipoSelecao,
          };

          setLoading(true);
          eventReactGA(SERVICO, 'Inativar serviço');
          await servicoApi.inactive(filtro);
          setLoading(false);

          resetSelecao();
          showSuccess(ids.length === 1 ? 'Serviço inativado com sucesso.' : 'Serviços inativados com sucesso.');
          fetchServicos(pageable);
        } catch (err) {
          apiUtils.handleApiError(err);
        }
      },
      opcoes: {
        title: 'Você realmente deseja inativar?',
        text: '',
      },
    });
  };

  const handleAtivar = (ids, pageable) => {
    excluirAlert({
      confirmar: async () => {
        try {
          const filtro = {
            ids: ids.map((_servico) => _servico.id),
            tipoSelecao,
            q: search,
          };

          setLoading(true);
          eventReactGA(SERVICO, 'Ativar serviço');
          await servicoApi.active(filtro);
          setLoading(false);

          resetSelecao();
          showSuccess(ids.length === 1 ? 'Serviço ativado com sucesso.' : 'Serviços ativados com sucesso.');
          fetchServicos(pageable);
        } catch (err) {
          apiUtils.handleApiError(err);
        }
      },
      opcoes: {
        title: 'Você realmente deseja ativar?',
        text: '',
      },
    });
  };

  const atualizarTour = async () => {
    try {
      setLoading(true);
      funcionalidadesTour.push('SERVICOS');
      await usuarioApi.updateTour(funcionalidadesTour);
      setRun(false);
      setTour(false);
      setFuncionalidadesTour(funcionalidadesTour);
      fetchServicos({ q: search, size, sort });
    } catch (err) {
      apiUtils.handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tour
        steps={steps}
        run={run}
        setRun={setRun}
        disableScrolling={true}
        onFinish={() => {
          atualizarTour();
        }}
      />
      <FusePageCarded
        innerScroll
        header={<ServicoSearchHeader />}
        content={
          <>
            <ServicoTable
              onSelection={handleSelectionChange}
              fetchServico={fetchServico}
              fetchServicos={fetchServicos}
              onDelete={handleDelete}
              onInativar={handleInativar}
              onAtivar={handleAtivar}
              onTour={onTour}
            />
            <VisualizarServicoModal />
            <EditarServicoModal
              onSubmit={handleSubmit}
              openLoadingModal={openLoadingModal}
              setOpenLoadingModal={setOpenLoadingModal}
              progressoLoading={progressoLoading}
              setProgressoLoading={setProgressoLoading}
              idNotificacao={idNotificacao}
              setFuncaoClose={(func) => (funcaoCloseRef.current = func)}
            />
            <ServicoBuscaAvancadaModal fetchServicos={fetchServicos} initialPage={initialPage} />
          </>
        }
      />
    </>
  );
};

const ServicoContainer = () => (
  <ServicoProvider>
    <Servico />
  </ServicoProvider>
);

export default ServicoContainer;
